import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { DetourSign, GuessSign, QuestionSign } from "../components/signs/RoadSigns";
import { Centered, Header, Shell } from "../components/Shell";
import { SuggestInput } from "../components/SuggestInput";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface Guess {
  name: string;
  km: number;
  dir: string;
}
interface View {
  country: { code: string; name: string };
  guesses: Guess[];
  hint?: string;
  answer?: string[];
}

const COPY = { unknown_capital: "Не знаю такої столиці - обери з підказок", already_guessed: "Вже було" };
/** The entry sign or the detour sign lands before the sheet. */
const OPTS = { sheetDelayMs: 1700 };

export function Stolytsia({ date }: { date?: string | undefined }) {
  const g = useGame<View>("stolytsia", COPY, date, OPTS);
  const restored = useRef<{ guesses: number; finished: boolean } | null>(null);
  const [names, setNames] = useState<{ uk: string; en: string }[]>([]);

  useEffect(() => {
    void api.names("stolytsia").then((r) => setNames(r.names.map((n) => ({ uk: n.uk, en: n.en })))).catch(() => {});
  }, []);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = new Set(state?.view.guesses.map((x) => x.name) ?? []);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  restored.current ??= { guesses: v.guesses.length, finished };
  const firstSeen = restored.current;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="СТОЛИЦЯ" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="px-4 pb-2 pt-1">
        <div className="mx-auto max-w-[420px]">
          <QuestionSign code={v.country.code} name={v.country.name} hint={v.hint} />
        </div>
        <div className="mt-1.5 text-center text-xs text-tghint">промах покаже, скільки кілометрів і куди до столиці</div>
      </div>

      <SuggestInput
        names={names}
        disabled={finished}
        busy={g.busy}
        exclude={guessed}
        placeholder="столиця…"
        onSubmit={(name) => void g.run(() => api.guess<View>("stolytsia", name, date))}
      />

      <div className="flex-1 space-y-2 overflow-y-auto px-4 pb-2 pt-2">
        {s.status === "lost" && v.answer && <DetourSign answer={v.answer} fresh={!firstSeen.finished} />}
        {v.guesses
          .map((guess, index) => ({ guess, index }))
          .reverse()
          .map(({ guess, index }) => (
            <GuessSign key={index} guess={guess} fresh={index >= firstSeen.guesses} />
          ))}
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <div className="flex flex-1 items-center text-xs text-tghint">спроб: {v.guesses.length}</div>
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("stolytsia", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Столиця взята!" : "Здався"}
          subtitle={
            won
              ? `за ${v.guesses.length} · стрік ${s.me.streak} 🔥`
              : `${v.answer?.join(" / ") ?? "?"} · стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n} спроб`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.answer && v.answer.length > 1 && (
            <div className="mt-3 text-center text-xs text-tghint">тут столиць кілька - рахувалась будь-яка</div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
